<template>
    <label :for="inputId" class="contact-form-label upload-document">
        {{ labelText }}
        
        <span v-if="!props.isRequired" class="optional-text"></span>
    </label>
    <div class="gibs-form-payment-upload-container">
        <div class="gibs-form-payment-upload-box justify-between" :id="inputId">
            <span class="text-wildblueyonder">Select document</span>
            <div class="gibs-custom-file-input" >
                <label for="fileInput" class="gibs-custom-file-upload-button">
                    <NuxtIcon name='buttons/upload' class="mr-2" /> {{ props.uploadText }}
                </label>
                <input multiple type="file" title=" " id="fileInput" @change="selectFile" accept=".pdf" ref="fileInput" />
            </div>
        </div>
    </div> 
    <p :id="`${props.inputId}-error`" class="gibs-form-group-error gibs-small-regular -mt-3">
        {{ displayFileErrorMessage }}</p>

    <div class="upload-file-list">
        <div v-for="(file, index) in fileList" :key="index" class="file-selection-options">
            <div class="level-left">
                <div class="level-item" :title="file.fileErrorMessage">
                    <NuxtIcon v-if="file.fileErrorMessage === ''" name="tags/doc-accept" class="doc-accept"/> 
                    <NuxtIcon v-else name="tags/doc-error" class="doc-error"/> 
                    {{ file.name }}
                </div>
            </div>
            <div class="level-right">
                <div class="level-item">
                    <button class="view" @click="viewFile(file.url)"><NuxtIcon name="buttons/view-mini" />VIEW</button>
                    <button class="delete" @click="deleteFile(index)"><NuxtIcon name="buttons/delete-mini" />DELETE</button>
                </div>
            </div>
        </div>

        <div v-for="(file, index) in props.currentFileList" :key="index" class="file-selection-options">
            <div class="level-left">
                <div class="level-item" :title="file.fileErrorMessage">
                    <NuxtIcon name="tags/doc-accept" class="doc-accept"/>
                    {{ file.fileName }}
                </div>
            </div>
        </div> 
    </div>
    <hr class="gibs-divider divider-2" />
</template>

<script lang="ts" setup scope>
    const fileInput = ref<HTMLInputElement>();

    const fileList = ref<Array<any>>([]);
    const fileErrorMessage = ref('');
    const displayFileErrorMessage = ref('');
    const store = useNotificationStore();
    let notification: INotification = {
        type: "",
        title: "",
        body: "",
        date: new Date(),
        timeout: true,
        timer: 10000
    }

    const emitFileUpload = defineEmits<{
        fileSelection: [file: Array<IFileUploadItem>]
    }>(); 

    const props = defineProps({
        itemModel: {
            type: Object as () => typeof FieldInfoItem,
            required: true,
        },
        inputId: {
            type: String,
            required: true,
        },
        labelText: {
            type: String,
            default: 'Upload Supporting Documents',
            required: true,
        },
        uploadText: {
            type: String,
            default: 'Upload',
            required: true,
        },
        isRequired: {
            type: Boolean,
            default: false,
        },
        currentFileList :{
            type: Array as () => z.infer<typeof any>,
            required: false,
        },
        validatorRuleId: {
			type: String,
			default: null
		},
        validator: {
			type: Validation,
			default: null
		},
    });
    
    function selectFile(event: Event) {
        //const _fileInput = event.target as any;
        //fileInput.files = _fileInput.files;
        //_fileInput.file = [];

        fileInput.value.files!.forEach((file: any) => {
            validateFiles(file);
            file.fileErrorMessage = fileErrorMessage.value;
            if (fileErrorMessage.value !== "") {
                displayFileErrorMessage.value = fileErrorMessage.value;
            }
            else{
                displayFileErrorMessage.value = "";
                file.url = URL.createObjectURL(file);
                fileList.value.push(file);
            }
        });
        
        if (typeof(props.validator) !== 'undefined' && props.validator != null) { 
            props.validator.setValidationRuleSourceValue(props.validatorRuleId, fileList.value);
            let resultList = props.validator.validateRule(props.validatorRuleId);
            ApplyValidate(!props.validator.isValidationRuleValid(props.validatorRuleId), resultList);
        }
        store.addNotification(notification);
        emitFileUpload('fileSelection', fileList.value);
        
        fileInput.value.value = null;
    }
    
    function ApplyValidate(isInvalid: boolean, validationResultList: Array<IValidationResult>) {
		const inputElement = document.getElementById(props.inputId) as HTMLElement;
		const errorElement = document.getElementById(`${props.inputId}-error`) as HTMLElement;

        if (inputElement) {
            if (isInvalid) {
                inputElement.classList.add("has-error");
            }
            else {
                inputElement.classList.remove("has-error");
            }
        }
		
		let errorString = '';
		if (typeof (props.validator) !== 'undefined' && props.validator != null) {
		    errorString = props.validator.getValidationResultString(props.validatorRuleId);
		}
		errorElement.innerText = errorString;
        if (!!errorString) {
            notification = {
                type: "error",
                title: "Document upload unsuccessful",
                body: `Failed to upload: ${errorString}`,
                date: new Date(),
                timeout: true,
                timer: 3000
            };
        }
	}

    function viewFile(url: string) {
        window.open(url, '_blank')!.focus();
    }

    function deleteFile(index: number) {
        let fileToDelete = fileList.value[index];
        fileList.value.splice(index, 1);
        emitFileUpload('fileSelection', fileList.value);
        notification = {
            type: "success",
            title: "Document delete",
            body: `"${fileToDelete.name}" has been deleted successfully.`,
            date: new Date(),
            timeout: true,
            timer: 10000
        };
        store.addNotification(notification);
    }

    function validateFiles(file: any) {
        const maxSize = 2000000;
        const allowedTypes = ["application/pdf"];
        if (!allowedTypes.includes(file.type)) {
            fileErrorMessage.value = "Incorrect document type. Only upload pdf."
            notification = {
                type: "error",
                title: "Document upload",
                body: `"${file.name}" failed to upload: ${fileErrorMessage.value}`,
                date: new Date(),
                timeout: true,
                timer: 10000
            };
        } else if (file.size > maxSize) {
            fileErrorMessage.value = `Max size: ${maxSize/1000000}MB`;
            notification = {
                type: "error",
                title: "Document upload",
                body: `"${file.name}" failed to upload: ${fileErrorMessage.value}`,
                date: new Date(),
                timeout: true,
                timer: 10000
            };
        } else {
            fileErrorMessage.value = "";
            notification = {
                type: "success",
                title: "Document upload",
                body: `"${file.name}" has been uploaded successfully.`,
                date: new Date(),
                timeout: true,
                timer: 10000
            };
        }
    }
</script>